import { codeQuizHandedInStatus } from "@/components/storyboard/quiz/codeQuizHandedIn";
import { useValidationSubmissions } from "@/hooks/useValidationSubmissions";
import { useCodeQuizProgress } from "@/hooks/useCodeQuizProgress";
import {
  inProgressFromSubmissions,
  type ValidationSubmission,
} from "@/lib/validationSubmission";
import type { InteractiveElementStatus } from "@/lib/interactiveElementState";

interface CappedCodeQuizArgs {
  /** Content element the submissions and the progress record belong to. */
  id: string;
  /** One task heading per entry, in entry order — the recap's question text. */
  prompts: string[];
  /** How the learner's answer is recapped; see codeQuizHandedInStatus. */
  answerKind?: "code" | "text";
  /** Highlighting for the recapped code, derived from the storyboard. */
  language?: string;
}

interface CappedCodeQuiz {
  submissions: Record<number, ValidationSubmission>;
  /** Revalidates the saved submissions after a new one is persisted. */
  refresh: () => void;
  /** Card status read off the server state, ahead of any local state. */
  statusOverride: InteractiveElementStatus | undefined;
  /** CTA that leaves the overlay once the quiz is over. */
  completedCtaLabel: string;
  /** True once every task is right or out of attempts, so the modal can close. */
  showCloseCta: boolean;
}

/**
 * Shared wiring for the attempt-capped code quizzes (CODE_ASSERTION,
 * CODE_DEBUG, CODE_EXPLANATION, CODE_FREETEXT): the learner's saved submissions
 * plus the card status and progress derived from them.
 *
 * Everything durable lives on the server, so a learner who clears their
 * browser storage — or comes back on another device — still meets the quiz in
 * the state they left it. The card stays a start card: "Quiz fortsetzen" with
 * Angefangen while a task can still be worked on, and "Ergebnis anzeigen"
 * once none can. The verdict itself stays in the modal.
 */
export const useCappedCodeQuiz = ({
  id,
  prompts,
  answerKind,
  language,
}: CappedCodeQuizArgs): CappedCodeQuiz => {
  const { submissions, refresh } = useValidationSubmissions(id);

  const handedIn = codeQuizHandedInStatus({
    prompts,
    submissions,
    answerKind,
    language,
  });

  useCodeQuizProgress(id, submissions, prompts.length, handedIn != null);

  return {
    submissions,
    refresh,
    // Keep the card reopenable even after the quiz is handed in. Local
    // progress is Completed by then, which is what relabels the button.
    statusOverride: inProgressFromSubmissions(submissions),
    completedCtaLabel: "Schließen",
    showCloseCta: handedIn != null,
  };
};
